import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import {
  api,
  useGetOpsAppObjectDefinitionsQuery,
  useListOpsAppObjectsQuery,
  useLazyListOpsAppObjectsQuery,
  useGetOpsAppObjectQuery,
  useLazyGetOpsAppObjectQuery,
  useQueryOpsAppObjectsQuery,
  useLazyQueryOpsAppObjectsQuery
} from "@webcore/services/api/api";
import {
  OpsAppObject,
  OpsAppObjectDocument,
  OpsAppObjectTypeDefinition,
  OpsAppObjectValue,
  ReserveSequenceNumberBlockResponse
} from "@webcore/services/api/models";

/** The platform-managed keys every flattened record carries alongside its declared properties. */
export interface OpsAppObjectMeta {
  id: string;
  sequenceNumber: number;
}

/** The untyped record shape — a flat map of primitive values — used when a read or write names no type argument. */
export type OpsAppObjectProperties = Record<string, OpsAppObjectValue>;

/**
 * A flattened record as custom view code sees it: the declared properties at the top level plus
 * the platform keys. `id` and `sequenceNumber` are reserved names, so a declared property never
 * collides with them.
 */
export type OpsAppObjectRecord<TItem extends object = OpsAppObjectProperties> = TItem & OpsAppObjectMeta;

/**
 * The result of a live read hook on any host API — the RTK Query shape, narrowed to the fields
 * custom view code needs. The hook re-renders and hands back a new result whenever what it read
 * changes (this session's own writes and remote SignalR-driven cache invalidations).
 */
export interface OpsAppQueryResult<TData> {
  /** The current data, or undefined before the first successful load. */
  data: TData | undefined;
  /** True only while loading for the first time (no cached data yet). */
  isLoading: boolean;
  /** True whenever a request is in flight, including background refetches after an invalidation. */
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  /** The error, when `isError`. */
  error: unknown;
  /** Force an immediate refetch. */
  refetch: () => void;
}

/** Fetches a page on demand; resolves the flattened records. */
export type OpsAppObjectsListTrigger<TItem extends object = OpsAppObjectProperties> = (tableName: string, skip: number, take: number, descending?: boolean) => Promise<OpsAppObjectRecord<TItem>[]>;

/** Fetches a single object on demand; resolves the flattened record, or null when it does not exist. */
export type OpsAppObjectGetTrigger<T extends object = OpsAppObjectProperties> = (tableName: string, id: string) => Promise<OpsAppObjectRecord<T> | null>;

/** Runs a server-side filtered page query on demand; resolves the flattened records. */
export type OpsAppObjectsQueryTrigger<TItem extends object = OpsAppObjectProperties> = (tableName: string, where: Record<string, OpsAppObjectValue>, skip: number, take: number, descending?: boolean) => Promise<OpsAppObjectRecord<TItem>[]>;

/**
 * The `opsAppObjects` member of the custom view host scope. Mirrors the ambient declarations that
 * custom view projects consume from their `src/globals.d.ts`.
 *
 * Every object is a JSON document validated against its type's schema. Every read and write takes
 * a type argument for the declared properties, so a view's data layer is strongly typed by its own
 * declarations (the repo build generates them from every type's schema). Without one, records are
 * the untyped flat primitive map. For a type with a `listProjection`, list and query reads return
 * only the projected root properties, so they are typed with a `Pick` of the document type; get and
 * upsert carry the whole document. A type with `ingestToBronze` also flows into bronze.
 *
 * Reads follow the RTK Query pattern. Each read has a **live** hook (auto-updates on change) and a
 * **lazy** hook (fetch on demand via the returned trigger, e.g. from an event handler). Call the
 * hooks at the top level of your view, following the rules of hooks.
 */
export interface OpsAppObjectsHostApi {
  /** The object types declared in the pipeline config (data-pipelines/main/objects/); read `schema`, `listProjection` and `ingestToBronze`. */
  readonly definitions: ReadonlyArray<OpsAppObjectTypeDefinition>;
  /** Server-resolved: whether the current session can write objects (Editor+ with write scope). */
  readonly canWrite: boolean;

  /**
   * Live query for a page of objects — the `take`-sized slice starting at `skip`, ordered by
   * sequenceNumber (ascending, or descending when `descending` is true). Auto-updates when the
   * object type changes. A type with a listProjection returns only the projected root properties.
   * There is no "get everything" call: an object type can hold a large data set, so
   * page yourself by advancing `skip` (a page shorter than `take` is the last one).
   */
  useListOpsAppObjects<TItem extends object = OpsAppObjectProperties>(tableName: string, skip: number, take: number, descending?: boolean): OpsAppQueryResult<OpsAppObjectRecord<TItem>[]>;
  /**
   * Live query for a single object by id — the whole document. Auto-updates when the object
   * changes. A missing object resolves to `isError` (a 404); check `isError` rather than expecting
   * `data === undefined`.
   */
  useGetOpsAppObject<T extends object = OpsAppObjectProperties>(tableName: string, id: string): OpsAppQueryResult<OpsAppObjectRecord<T>>;

  /**
   * Lazy version of {@link useListOpsAppObjects}: returns `[fetchPage, result]`. Call `fetchPage`
   * on demand; `result` then auto-updates like the live query.
   */
  useLazyListOpsAppObjects<TItem extends object = OpsAppObjectProperties>(): [OpsAppObjectsListTrigger<TItem>, OpsAppQueryResult<OpsAppObjectRecord<TItem>[]>];
  /**
   * Lazy version of {@link useGetOpsAppObject}: returns `[fetchObject, result]`. `fetchObject`
   * resolves null when the object does not exist.
   */
  useLazyGetOpsAppObject<T extends object = OpsAppObjectProperties>(): [OpsAppObjectGetTrigger<T>, OpsAppQueryResult<OpsAppObjectRecord<T>>];

  /**
   * Live server-side filtered query: equality on root properties with primitive values (AND
   * semantics; null matches null/unset), ordered by sequenceNumber and paged and projected like
   * {@link useListOpsAppObjects}. Datetime and date values compare as exact ISO strings. Results
   * refresh once a write is confirmed by the server (filtered results are not patched
   * optimistically).
   */
  useQueryOpsAppObjects<TItem extends object = OpsAppObjectProperties>(tableName: string, where: Record<string, OpsAppObjectValue>, skip: number, take: number, descending?: boolean): OpsAppQueryResult<OpsAppObjectRecord<TItem>[]>;
  /**
   * Lazy version of {@link useQueryOpsAppObjects}: returns `[runQuery, result]`.
   */
  useLazyQueryOpsAppObjects<TItem extends object = OpsAppObjectProperties>(): [OpsAppObjectsQueryTrigger<TItem>, OpsAppQueryResult<OpsAppObjectRecord<TItem>[]>];

  /**
   * Inserts or replaces an object — the whole document, validated against the type's schema. Mints
   * an id when the object carries none and resolves the stored object in full. Replacements of objects already in
   * cached reads update optimistically — live query hooks reflect the write immediately, before the
   * server confirms (and roll back if the write fails). Newly inserted objects appear once the
   * server confirms and the refetch lands, since their sequence position isn't known client-side.
   */
  upsert<T extends object = OpsAppObjectProperties>(tableName: string, object: T & { id?: string }): Promise<OpsAppObjectRecord<T>>;
  /**
   * Deletes an object; resolves whether it existed. Cached reads update optimistically — live
   * query hooks drop the object immediately, before the server confirms (and roll back if the
   * delete fails).
   */
  remove(tableName: string, id: string): Promise<boolean>;
  /**
   * Atomically reserves a gapless block of numbers on the named counter `propertyName` of the
   * object type `objectName`. `blockLength` defaults to 1. Reserved numbers are consumed on
   * return and never reused, even if the caller discards them. Requires `canWrite`.
   */
  reserveSequenceNumberBlock(objectName: string, propertyName: string, blockLength?: number): Promise<ReserveSequenceNumberBlockResponse>;
}

type ApiDispatch = ThunkDispatch<Record<string, unknown>, unknown, UnknownAction>;

// The server validated the stored document against the type's definition, and the caller's type
// argument describes that definition, so the document is asserted to it rather than re-checked.
function flattenObject<TItem extends object>(item: OpsAppObject): OpsAppObjectRecord<TItem> {
  return {
    ...(item.properties as TItem),
    id: item.id,
    sequenceNumber: item.sequenceNumber
  };
}

export function isNotFoundError(error: unknown): boolean {
  return typeof error === "object" && error !== null && "status" in error
    && (error as { status: unknown }).status === 404;
}

// The host read hooks: thin wrappers over the shared RTK Query hooks that flatten each stored
// object to an OpsAppObjectRecord. Each is produced by a factory that closes over the renderer's
// dataPipelineConfigurationId, so the id is transparent to custom view code (mirroring
// fetchKpiData). The returned functions are plain custom hooks — the view calls them inside its
// own render, so they resolve the host app's store context just like any RTK Query hook.

function createUseListObjects(dataPipelineConfigurationId: string) {
  return function useListObjects<TItem extends object = OpsAppObjectProperties>(tableName: string, skip: number, take: number, descending: boolean = false): OpsAppQueryResult<OpsAppObjectRecord<TItem>[]> {
    const result = useListOpsAppObjectsQuery({ dataPipelineConfigurationId, tableName, skip, take, descending });
    const data = useMemo(() => result.data?.items.map(item => flattenObject<TItem>(item)), [result.data]);
    return { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch: result.refetch };
  };
}

function createUseGetObject(dataPipelineConfigurationId: string) {
  return function useGetObject<T extends object = OpsAppObjectProperties>(tableName: string, id: string): OpsAppQueryResult<OpsAppObjectRecord<T>> {
    const result = useGetOpsAppObjectQuery({ dataPipelineConfigurationId, tableName, objectId: id });
    const data = useMemo(() => (result.data ? flattenObject<T>(result.data) : undefined), [result.data]);
    return { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch: result.refetch };
  };
}

function createUseLazyListObjects(dataPipelineConfigurationId: string) {
  return function useLazyListObjects<TItem extends object = OpsAppObjectProperties>(): [OpsAppObjectsListTrigger<TItem>, OpsAppQueryResult<OpsAppObjectRecord<TItem>[]>] {
    // Lazy query state carries no refetch (lazy queries re-run through the trigger); refetch re-issues
    // the last-triggered args, forcing a network round-trip (preferCacheValue: false).
    const [trigger, result, { lastArg }] = useLazyListOpsAppObjectsQuery();
    const data = useMemo(() => result.data?.items.map(item => flattenObject<TItem>(item)), [result.data]);
    const fetchPage = useCallback<OpsAppObjectsListTrigger<TItem>>(
      async (tableName, skip, take, descending = false) => {
        const response = await trigger({ dataPipelineConfigurationId, tableName, skip, take, descending }).unwrap();
        return response.items.map(item => flattenObject<TItem>(item));
      },
      [trigger]
    );
    const refetch = useCallback(() => {
      if (lastArg !== undefined) {
        void trigger(lastArg, false);
      }
    }, [trigger, lastArg]);
    return [fetchPage, { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch }];
  };
}

function createUseLazyGetObject(dataPipelineConfigurationId: string) {
  return function useLazyGetObject<T extends object = OpsAppObjectProperties>(): [OpsAppObjectGetTrigger<T>, OpsAppQueryResult<OpsAppObjectRecord<T>>] {
    const [trigger, result, { lastArg }] = useLazyGetOpsAppObjectQuery();
    const data = useMemo(() => (result.data ? flattenObject<T>(result.data) : undefined), [result.data]);
    const fetchObject = useCallback<OpsAppObjectGetTrigger<T>>(
      async (tableName, id) => {
        try {
          const item = await trigger({ dataPipelineConfigurationId, tableName, objectId: id }).unwrap();
          return flattenObject<T>(item);
        } catch (error) {
          if (isNotFoundError(error)) {
            return null;
          }
          throw error;
        }
      },
      [trigger]
    );
    const refetch = useCallback(() => {
      if (lastArg !== undefined) {
        void trigger(lastArg, false);
      }
    }, [trigger, lastArg]);
    return [fetchObject, { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch }];
  };
}

function createUseQueryObjects(dataPipelineConfigurationId: string) {
  return function useQueryObjects<TItem extends object = OpsAppObjectProperties>(tableName: string, where: Record<string, OpsAppObjectValue>, skip: number, take: number, descending: boolean = false): OpsAppQueryResult<OpsAppObjectRecord<TItem>[]> {
    const result = useQueryOpsAppObjectsQuery({ dataPipelineConfigurationId, tableName, where, skip, take, descending });
    const data = useMemo(() => result.data?.items.map(item => flattenObject<TItem>(item)), [result.data]);
    return { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch: result.refetch };
  };
}

function createUseLazyQueryObjects(dataPipelineConfigurationId: string) {
  return function useLazyQueryObjects<TItem extends object = OpsAppObjectProperties>(): [OpsAppObjectsQueryTrigger<TItem>, OpsAppQueryResult<OpsAppObjectRecord<TItem>[]>] {
    const [trigger, result, { lastArg }] = useLazyQueryOpsAppObjectsQuery();
    const data = useMemo(() => result.data?.items.map(item => flattenObject<TItem>(item)), [result.data]);
    const runQuery = useCallback<OpsAppObjectsQueryTrigger<TItem>>(
      async (tableName, where, skip, take, descending = false) => {
        const response = await trigger({ dataPipelineConfigurationId, tableName, where, skip, take, descending }).unwrap();
        return response.items.map(item => flattenObject<TItem>(item));
      },
      [trigger]
    );
    const refetch = useCallback(() => {
      if (lastArg !== undefined) {
        void trigger(lastArg, false);
      }
    }, [trigger, lastArg]);
    return [runQuery, { data, isLoading: result.isLoading, isFetching: result.isFetching, isSuccess: result.isSuccess, isError: result.isError, error: result.error, refetch }];
  };
}

/**
 * Builds the `opsAppObjects` host API injected into custom view code. Returns null until the
 * definitions have loaded so renderers can gate execution on a fully-initialized scope.
 *
 * Reads are the shared RTK Query hooks (flattened to `OpsAppObjectRecord`); writes are imperative
 * mutations that optimistically patch the cached read results (rolled back on failure), so live
 * hooks reflect a write immediately. The `OpsAppObjects` cache tag then reconciles everything
 * authoritatively: upserts/removes invalidate it, SignalR change notifications (direct or
 * MFE-forwarded) invalidate it, and every live read hook refetches on invalidation and re-renders
 * its caller.
 */
export function useOpsAppObjectsHostApi(dataPipelineConfigurationId: string): OpsAppObjectsHostApi | null {
  const dispatch = useDispatch<ApiDispatch>();
  const { data: definitionsResponse } = useGetOpsAppObjectDefinitionsQuery({ dataPipelineConfigurationId });

  return useMemo(() => {
    if (definitionsResponse === undefined) {
      return null;
    }

    return {
      definitions: definitionsResponse.objectTypes,
      canWrite: definitionsResponse.canWrite,

      useListOpsAppObjects: createUseListObjects(dataPipelineConfigurationId),
      useGetOpsAppObject: createUseGetObject(dataPipelineConfigurationId),
      useLazyListOpsAppObjects: createUseLazyListObjects(dataPipelineConfigurationId),
      useLazyGetOpsAppObject: createUseLazyGetObject(dataPipelineConfigurationId),
      useQueryOpsAppObjects: createUseQueryObjects(dataPipelineConfigurationId),
      useLazyQueryOpsAppObjects: createUseLazyQueryObjects(dataPipelineConfigurationId),

      upsert: async <T extends object = OpsAppObjectProperties>(tableName: string, object: T & { id?: string }): Promise<OpsAppObjectRecord<T>> => {
        const { id, ...properties } = object;
        const objectId = typeof id === "string" && id.length > 0 ? id : crypto.randomUUID();
        const item = await dispatch(api.endpoints.upsertOpsAppObject.initiate(
          { dataPipelineConfigurationId, tableName, objectId, properties: properties as OpsAppObjectDocument }
        )).unwrap();
        return flattenObject<T>(item);
      },

      remove: async (tableName: string, id: string): Promise<boolean> => {
        const response = await dispatch(api.endpoints.deleteOpsAppObject.initiate(
          { dataPipelineConfigurationId, tableName, objectId: id }
        )).unwrap();
        return response.deleted;
      },

      reserveSequenceNumberBlock: async (objectName: string, propertyName: string, blockLength: number = 1): Promise<ReserveSequenceNumberBlockResponse> =>
        await dispatch(api.endpoints.reserveOpsAppObjectSequenceNumberBlock.initiate(
          { dataPipelineConfigurationId, tableName: objectName, propertyName, blockLength }
        )).unwrap()
    };
  }, [definitionsResponse, dispatch, dataPipelineConfigurationId]);
}
